import React, { useEffect, useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { differenceInDays } from 'date-fns';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Image from '@atlaskit/image';
import IconButton from '@atlaskit/button/icon/button';
import CrossIcon from '@atlaskit/icon/core/cross';
import Heading from '@atlaskit/heading/heading';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { token } from '@atlaskit/tokens';

import {
	useSetOnboardingState,
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { USE_CASES_NTH_USER_ONBOARDING } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import { usePTV2State } from '../usePTV2Store.tsx';

import welcomeBannerIllustrationLight from './assets/WelcomeBannerIllustrationLight.svg';
import welcomeBannerIllustrationDark from './assets/WelcomeBannerIllustrationDark.svg';

const i18n = defineMessages({
	useCasesHomeWelcomeBannerTitle: {
		id: 'experiment-personalized-templates-v2.use-cases-in-nth-user-onboarding.welcome-banner.title',
		defaultMessage: 'Welcome to Confluence, {name}',
		description: 'Title of the Use cases in Nth User Onboarding Welcome Banner on home',
	},
	useCasesHomeWelcomeBannerAction: {
		id: 'experiment-personalized-templates-v2.use-cases-in-nth-user-onboarding.welcome-banner.action',
		defaultMessage:
			'Get started with a template below, or click <b>Create</b> to start from scratch.',
		description: 'Action of the Use cases in Nth User Onboarding Welcome Banner on home',
	},
	useCasesHomeWelcomeBannerDismiss: {
		id: 'experiment-personalized-templates-v2.use-cases-in-nth-user-onboarding.welcome-banner.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Dismiss button of the Use cases in Nth User Onboarding Welcome Banner on home',
	},
});

const styles = cssMap({
	container: {
		position: 'relative',
		minHeight: '130px',
		borderRadius: token('radius.large'),
	},
	textSection: {
		paddingTop: token('space.400'),
		paddingBottom: token('space.400'),
		paddingLeft: token('space.400'),
		paddingRight: token('space.500'),
		gap: token('space.100'),
	},
	dismiss: {
		top: token('space.200'),
		right: token('space.200'),
		position: 'absolute',
		cursor: 'pointer',
	},
});

type UseCasesHomeWelcomeBannerProps = {
	name: string;
};

//TODO: REfactor this
export const UseCasesHomeWelcomeBanner = ({
	name,
}: UseCasesHomeWelcomeBannerProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { setOnboardingState } = useSetOnboardingState();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isTemplateListDismissed } = usePTV2State();

	//TODO: Fix this
	const { data, loading, error } = useGetOnboardingState(
		USE_CASES_NTH_USER_ONBOARDING.VIEWED_HEADER,
	);

	const { headerViewedAt } = deserializeState(data);

	const onboardingPeriodDay = headerViewedAt
		? differenceInDays(new Date(), new Date(headerViewedAt))
		: 0;

	useEffect(() => {
		if (!headerViewedAt) {
			setOnboardingState({
				key: USE_CASES_NTH_USER_ONBOARDING.VIEWED_HEADER,
				value: new Date().toISOString(),
			});

			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'useCasesHomeWelcomeBanner',
					attributes: {
						onboardingPeriodDay,
					},
				},
			}).fire();
		}
	}, [createAnalyticsEvent, setOnboardingState, headerViewedAt, onboardingPeriodDay]);

	const handleDismiss = useCallback(() => {
		setOnboardingState({
			key: USE_CASES_NTH_USER_ONBOARDING.DISMISSED_HEADER,
			value: 'true',
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'useCasesHomeWelcomeBannerDismiss',
				source: 'cc-experiment-use-cases-in-nth-user-onboarding',
				attributes: {
					onboardingPeriodDay,
				},
			},
		}).fire();
	}, [setOnboardingState, createAnalyticsEvent, onboardingPeriodDay]);

	// Do not show the banner under the following criteria:
	// 1. If the banner has already been dismissed (this is handled in useOnboardingTakeoverType)
	// 2. We are outside of the 14 day onboarding period (this is handled in useOnboardingTakeoverType)
	// 3. The templates list is already dismissed
	if (isTemplateListDismissed || loading || error) {
		return null;
	}

	return (
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
		<Box backgroundColor="color.background.accent.blue.subtlest" xcss={styles.container}>
			<Inline alignBlock="end">
				<Stack xcss={styles.textSection}>
					<Heading size="large">
						<FormattedMessage {...i18n.useCasesHomeWelcomeBannerTitle} values={{ name }} />
					</Heading>
					<Text>
						<FormattedMessage
							{...i18n.useCasesHomeWelcomeBannerAction}
							values={{ b: (words: React.ReactNode) => <b>{words}</b> }}
						/>
					</Text>
				</Stack>
				<Image
					src={welcomeBannerIllustrationLight}
					srcDark={welcomeBannerIllustrationDark}
					alt=""
				/>
			</Inline>
			<Box xcss={styles.dismiss}>
				<IconButton
					onClick={handleDismiss}
					spacing="default"
					appearance="subtle"
					icon={CrossIcon}
					label={formatMessage(i18n.useCasesHomeWelcomeBannerDismiss)}
					isTooltipDisabled={false}
				/>
			</Box>
		</Box>
	);
};
